import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { getProject } from 'Services/project';
import {
  fetchTournamentAwards,
  getAwardOrderField,
} from '../../../tournamentAwards/actions';
import TeamAwardsList from './TeamAwardsList';

@injectIntl
@connect()
export default class TeamAwards extends Component {
  state = {
    winnerAwards: [],
    runnerUpAwards: [],
    winnerPagination: undefined,
    runnerUpPagination: undefined,
    winnerTotal: 0,
    runnerUpTotal: 0,
    fetching: false,
    loading: true,
    activeTab: 'winner',
    query: {
      sort: { field: 'tournamentStartDate', order: 'desc' },
      pagination: { page: 1, itemsPerPage: 10 }
    }
  };

  componentDidMount() {
    const { teamId } = this.props;
    const { query } = this.state;

    // Load both tabs up front so each badge count is correct from the start. Each fetch reads its
    // own resolved response (not a shared redux slot), so the two parallel calls don't clobber.
    Promise.all([
      this.fetchWinnerAwards(teamId, query),
      this.fetchRunnerUpAwards(teamId, query)
    ]).then(() => this.setState({ loading: false }));
  }

  fetchWinnerAwards = (teamId, query) => {
    const { dispatch } = this.props;
    const params = {
      'winnerTeam.id': teamId,
      itemsPerPage: query.pagination.itemsPerPage,
      page: query.pagination.page,
      [`order[${getAwardOrderField(query.sort.field)}]`]: query.sort.order
    };

    this.setState({ fetching: true });
    return dispatch(fetchTournamentAwards(params))
      .then(data => {
        const hasData = data && Array.isArray(data.collection);
        const collection = hasData ? data.collection : [];
        // Some filtered award queries omit hydra:totalItems -> fall back to the page length.
        const total = hasData && data.totalItems != null ? data.totalItems : collection.length;
        this.setState({
          winnerAwards: collection,
          winnerPagination: hasData ? data.pagination : undefined,
          winnerTotal: total,
          fetching: false
        });
      });
  };

  fetchRunnerUpAwards = (teamId, query) => {
    const { dispatch } = this.props;
    const params = {
      'runnerUpTeam.id': teamId,
      itemsPerPage: query.pagination.itemsPerPage,
      page: query.pagination.page,
      [`order[${getAwardOrderField(query.sort.field)}]`]: query.sort.order
    };

    this.setState({ fetching: true });
    return dispatch(fetchTournamentAwards(params))
      .then(data => {
        const hasData = data && Array.isArray(data.collection);
        const collection = hasData ? data.collection : [];
        // Some filtered award queries omit hydra:totalItems -> fall back to the page length.
        const total = hasData && data.totalItems != null ? data.totalItems : collection.length;
        this.setState({
          runnerUpAwards: collection,
          runnerUpPagination: hasData ? data.pagination : undefined,
          runnerUpTotal: total,
          fetching: false
        });
      });
  };

  refetchActiveTab = (teamId, query) => {
    const { activeTab } = this.state;
    if (activeTab === 'winner') {
      this.fetchWinnerAwards(teamId, query);
    } else {
      this.fetchRunnerUpAwards(teamId, query);
    }
  };

  handlePagination = change => {
    const { teamId } = this.props;
    const { query } = this.state;

    const newQuery = {
      ...query,
      pagination: {
        ...query.pagination,
        ...(change.page && { page: change.page }),
        ...(change.itemsPerPage && { itemsPerPage: change.itemsPerPage })
      }
    };

    this.setState({ query: newQuery });
    this.refetchActiveTab(teamId, newQuery);
  };

  handleSort = field => {
    const { teamId } = this.props;
    const { query } = this.state;

    // Toggle sort order if same field, otherwise set to asc
    const order = query.sort.field === field && query.sort.order === 'asc' ? 'desc' : 'asc';

    const newQuery = {
      ...query,
      sort: { field, order }
    };

    this.setState({ query: newQuery });
    this.refetchActiveTab(teamId, newQuery);
  };

  handleTabChange = (tab) => {
    const { teamId } = this.props;
    const { query, activeTab } = this.state;

    if (tab === activeTab) {
      return;
    }

    // Reset pagination when changing tabs, then refresh that tab's data.
    const newQuery = {
      ...query,
      pagination: {
        ...query.pagination,
        page: 1
      }
    };

    this.setState({ query: newQuery, activeTab: tab });

    if (tab === 'winner') {
      this.fetchWinnerAwards(teamId, newQuery);
    } else {
      this.fetchRunnerUpAwards(teamId, newQuery);
    }
  };

  goToCreateAward = () => {
    const { teamId, history } = this.props;

    history.push(`${getProject().url}/tournament-awards/new?winnerTeam=${teamId}`);
  };

  goToEditAward = award => {
    this.props.history.push(`${getProject().url}/tournament-awards/${award.id}`);
  };

  renderAddAward = () => {
    const { intl: { messages: m } } = this.props;

    return (
      <div style={{ padding: 16 }}>
        <Button color="primary" variant="outlined" onClick={this.goToCreateAward}>
          {m.button_add}
        </Button>
      </div>
    );
  };

  render() {
    const { intl: { messages: m } } = this.props;
    const {
      winnerAwards, runnerUpAwards,
      winnerPagination, runnerUpPagination,
      winnerTotal, runnerUpTotal,
      fetching, loading, activeTab, query
    } = this.state;

    // Show loading indicator while fetching initial data
    if (loading) {
      return (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <Typography variant="body1">
            Chargement...
          </Typography>
        </div>
      );
    }

    // Show message when no awards found (but keep the add action available)
    if (winnerAwards.length === 0 && runnerUpAwards.length === 0 && !fetching) {
      return (
        <div>
          {this.renderAddAward()}
          <div style={{ padding: 16, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              {m.error_noData}
            </Typography>
          </div>
        </div>
      );
    }

    // Create tabs for winner and runner-up awards
    const tabs = [
      { id: 'winner', label: m.field_winner, count: winnerTotal },
      { id: 'runnerUp', label: m.field_runnerUp, count: runnerUpTotal }
    ];

    const activeAwards = activeTab === 'winner' ? winnerAwards : runnerUpAwards;
    const activePagination = activeTab === 'winner' ? winnerPagination : runnerUpPagination;
    const activeTotal = activeTab === 'winner' ? winnerTotal : runnerUpTotal;

    return (
      <div>
        {this.renderAddAward()}
        <div style={{ display: 'flex', marginBottom: 16, borderBottom: '1px solid #eee' }}>
          {tabs.map(tab => (
            <div
              key={tab.id}
              onClick={() => this.handleTabChange(tab.id)}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                borderBottom: activeTab === tab.id ? '2px solid #3f51b5' : 'none'
              }}
            >
              {tab.label} ({tab.count})
            </div>
          ))}
        </div>

        <TeamAwardsList
          awards={activeAwards}
          loading={fetching}
          type={activeTab}
          query={query}
          pagination={activePagination}
          total={activeTotal}
          onPaginate={this.handlePagination}
          onSort={this.handleSort}
          onRowClick={this.goToEditAward}
        />
      </div>
    );
  }
}
